import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";
import { chatClient } from "@core/clients";
import { OrderChatMessage, OrderChatMessagesPage, SendOrderChatMessagePayload } from "@core/types";
import { useAuth } from "../../contexts/auth-context";
import { orderChatQueryKeys } from "./keys";
import { mergeNewMessageIntoCache, replaceOptimisticMessage, setOptimisticMessageStatus } from "./utils";

type Context = { clientId: string };

const generateClientId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `c_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const buildOptimisticMessage = (
  variables: SendOrderChatMessagePayload,
  clientId: string,
  senderCustomerId: number | null,
): OrderChatMessage => ({
  id: -Date.now(),
  clientId,
  chatId: variables.chatId,
  senderType: "customer",
  senderCustomerId,
  senderAdminUserId: null,
  messageType: "text",
  text: variables.text,
  createdAt: new Date().toISOString(),
  deletedAt: null,
  attachments: [],
  status: "sending",
});
const useSendOrderChatMessageMutation = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const senderCustomerId = user ? Number(user.id) || null : null;

  return useMutation<OrderChatMessage, unknown, SendOrderChatMessagePayload, Context>({
    mutationKey: ["chat", "send-message"],
    mutationFn: (payload) => chatClient.sendOrderChatMessage(payload),
    onMutate: (variables) => {
      const clientId = generateClientId();
      const optimistic = buildOptimisticMessage(variables, clientId, senderCustomerId);

      queryClient.setQueryData<InfiniteData<OrderChatMessagesPage>>(
        orderChatQueryKeys.messages(variables.chatId),
        (cache) => mergeNewMessageIntoCache(cache, optimistic),
      );

      return { clientId };
    },
    onSuccess: (serverMessage, variables, context) => {
      if (!context) return;
      queryClient.setQueryData<InfiniteData<OrderChatMessagesPage>>(
        orderChatQueryKeys.messages(variables.chatId),
        (cache) => replaceOptimisticMessage(cache, context.clientId, serverMessage),
      );
    },
    onError: (_error, variables, context) => {
      if (!context) return;
      queryClient.setQueryData<InfiniteData<OrderChatMessagesPage>>(
        orderChatQueryKeys.messages(variables.chatId),
        (cache) => setOptimisticMessageStatus(cache, context.clientId, "failed"),
      );
    },
  });
};

export default useSendOrderChatMessageMutation;
